import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Flex_Container_Examples() {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📦 Flex Container Advanced Examples</h1>

      <p className="tutorial-intro">
        Learn multiple flex container layouts from basic row layouts to complex
        responsive grids, nested flex containers, and adaptive designs.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Flex container controls alignment, direction, and wrapping of child
            items.
          </li>
          <li>
            Combine <code>justify-content</code>, <code>align-items</code>, and{" "}
            <code>align-content</code> for powerful layouts.
          </li>
          <li>
            Use <code>flex-wrap</code> for responsive multi-row designs.
          </li>
          <li>Nested flex containers allow complex layouts.</li>
        </ul>
      </div>

      {/* Example 1: Simple Row Layout */}
      <h2 className="section-title">1️⃣ Simple Row Layout</h2>
      <CodeCopyBlock
        code={`.flex-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}`}
      />
      <div
        className="flex-row"
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            background: "#3b82f6",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Box 1
        </div>
        <div
          style={{
            background: "#2563eb",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Box 2
        </div>
        <div
          style={{
            background: "#60a5fa",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Box 3
        </div>
      </div>

      {/* Example 2: Column Layout */}
      <h2 className="section-title">2️⃣ Column Layout</h2>
      <CodeCopyBlock
        code={`.flex-column {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 15px;
}`}
      />
      <div
        className="flex-column"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "15px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            background: "#f97316",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Box A
        </div>
        <div
          style={{
            background: "#ea580c",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Box B
        </div>
        <div
          style={{
            background: "#fbbf24",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Box C
        </div>
      </div>

      {/* Example 3: Nested Flex */}
      <h2 className="section-title">3️⃣ Nested Flex Containers</h2>
      <CodeCopyBlock
        code={`.outer-flex {
  display: flex;
  gap: 20px;
}
.inner-flex {
  display: flex;
  flex-direction: column;
  gap: 10px;
}`}
      />
      <div
        className="outer-flex"
        style={{ display: "flex", gap: "20px", marginBottom: "2rem" }}
      >
        <div
          className="inner-flex"
          style={{ display: "flex", flexDirection: "column", gap: "10px" }}
        >
          <div
            style={{
              background: "#60a5fa",
              color: "white",
              padding: "20px",
              borderRadius: "6px",
            }}
          >
            Item 1
          </div>
          <div
            style={{
              background: "#2563eb",
              color: "white",
              padding: "20px",
              borderRadius: "6px",
            }}
          >
            Item 2
          </div>
        </div>
        <div
          className="inner-flex"
          style={{ display: "flex", flexDirection: "column", gap: "10px" }}
        >
          <div
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "20px",
              borderRadius: "6px",
            }}
          >
            Item 3
          </div>
          <div
            style={{
              background: "#1e40af",
              color: "white",
              padding: "20px",
              borderRadius: "6px",
            }}
          >
            Item 4
          </div>
        </div>
      </div>

      {/* Example 4: Flex Wrap */}
      <h2 className="section-title">4️⃣ Flex Wrap Example</h2>
      <CodeCopyBlock
        code={`.flex-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}`}
      />
      <div
        className="flex-wrap"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "15px",
          marginBottom: "2rem",
        }}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#f43f5e",
              color: "white",
              padding: "30px",
              borderRadius: "8px",
              flex: "1 1 150px",
            }}
          >
            Box {i + 1}
          </div>
        ))}
      </div>

      {/* Example 5: Justify Content Variations */}
      <h2 className="section-title">5️⃣ Justify Content Variations</h2>
      <CodeCopyBlock
        code={`.justify-center {
  display: flex;
  justify-content: center;
  gap: 10px;
}
.justify-space-between {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}`}
      />
      <div
        className="justify-center"
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          marginBottom: "1rem",
        }}
      >
        <div
          style={{
            background: "#10b981",
            color: "white",
            padding: "20px",
            borderRadius: "6px",
          }}
        >
          Center 1
        </div>
        <div
          style={{
            background: "#059669",
            color: "white",
            padding: "20px",
            borderRadius: "6px",
          }}
        >
          Center 2
        </div>
        <div
          style={{
            background: "#047857",
            color: "white",
            padding: "20px",
            borderRadius: "6px",
          }}
        >
          Center 3
        </div>
      </div>
      <div
        className="justify-space-between"
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            background: "#facc15",
            color: "black",
            padding: "20px",
            borderRadius: "6px",
          }}
        >
          Start
        </div>
        <div
          style={{
            background: "#eab308",
            color: "black",
            padding: "20px",
            borderRadius: "6px",
          }}
        >
          Middle
        </div>
        <div
          style={{
            background: "#ca8a04",
            color: "black",
            padding: "20px",
            borderRadius: "6px",
          }}
        >
          End
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fcd34d)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with <strong>align-items</strong> and{" "}
            <strong>align-content</strong> for vertical alignment.
          </li>
          <li>
            Try <strong>nested flex containers</strong> with multiple
            directions.
          </li>
          <li>
            Combine <strong>flex-wrap</strong> with{" "}
            <strong>media queries</strong> for responsive layouts.
          </li>
          <li>
            Use <strong>flex-grow</strong>, <strong>flex-shrink</strong>, and{" "}
            <strong>flex-basis</strong> to control individual item sizes.
          </li>
          <li>
            Create complex UI components like cards, navbars, and galleries
            using flex containers.
          </li>
        </ul>
      </div>
    </div>
  );
}
